import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { ensureValidAuth, refreshAuth } from "./auth";
import { JWT } from "./types";

/**
 * Shared promise to prevent concurrent refresh calls.
 * This ensures that if multiple requests encounter
 * an expired token at the same time, only one refresh
 * request is sent to the server.
 */
let refreshPromise: Promise<JWT> | null = null;

/**
 * Refresh the access token if not already refreshing.
 *
 * This function retrieves the necessary configuration and refresh token from the
 * SDK configuration store, then calls `refreshAuth`. It uses a shared promise
 * to ensure that only one token refresh operation is active at any given time,
 * preventing multiple concurrent refresh requests.
 *
 * @returns A Promise that resolves to a JWT object once the refresh is complete.
 * @throws {Error} If the SDK is not initialized, the refresh token is missing,
 *   or the token refresh operation fails.
 */
async function refreshTokenIfNeeded(): Promise<JWT> {
  if (!refreshPromise) {
    // Get the current state directly inside the function scope to ensure it's up-to-date
    const { jwt, tenantId, apiKey, baseUrl, apiPath } =
      useSDKConfigStore.getState();

    if (!jwt?.refreshToken) {
      throw new Error("No refresh token available in SDK configuration store.");
    }
    if (!jwt?.eoa || !tenantId || !apiKey || !baseUrl || !apiPath) {
      throw new Error("Missing required SDK configuration for token refresh.");
    }

    refreshPromise = refreshAuth(jwt.refreshToken, {
      eoaAddress: jwt.eoa,
      tenantId,
      apiKey,
      baseUrl,
      apiPath,
    }).finally(() => {
      // Clear the promise once it settles, regardless of success or failure
      refreshPromise = null;
    });
  }
  return refreshPromise;
}
/**
 * Apply authentication + partner headers to request config.
 */
function applyAuthHeaders(config: InternalAxiosRequestConfig) {
  const { jwt, apiKey, tenantId } = useSDKConfigStore.getState();

  if (!apiKey) throw new Error("Missing API key.");
  if (!jwt?.accessToken) throw new Error("Missing access token.");

  config.headers.set("Authorization", `Bearer ${jwt.accessToken}`);
  config.headers.set("X-Api-Key", apiKey);
  config.headers.set("Content-Type", "application/json");
  config.headers.set("ngrok-skip-browser-warning", "true");

  if (tenantId) {
    config.headers.set("X-Partner-ID", tenantId);
  }

  return config;
}

/**
 * Get a preconfigured Axios API client for SwypeSDK.
 *
 * ### Features:
 * 1. **Automatic Token Validation**
 *    Runs `ensureValidAuth()` before each request to verify token freshness.
 *
 * 2. **Automatic Token Refresh**
 *    If token is expired or missing, it calls `refreshAuth()` and updates headers automatically.
 *
 * 3. **Concurrency-safe Refresh**
 *    Uses a `refreshPromise` guard so only one refresh request is sent at a time.
 *
 * 4. **Fallback Retry on Auth Error**
 *    If a request fails with 401/403, it attempts one refresh + retry before failing.
 *
 * 5. **Centralized Header Management**
 *    All header setting happens in `applyAuthHeaders()` — no duplication.
 *
 * @throws If SDK is not initialized or no valid token exists after refresh.
 *
 * @example
 * ```ts
 * const client = getApiClient();
 * const res = await client.get("/users/me");
 * console.log(res.data);
 * ```
 */
export function getApiClient(): AxiosInstance {
  const { isInitialized, apiKey, jwt, baseUrl, apiPath, clearJWT } =
    useSDKConfigStore.getState();

  // Fail early if SDK is not ready
  if (!isInitialized || !apiKey) {
    throw new Error("SwypeSDK not initialized. Call initializeSwypeSDK first.");
  }
  if (!jwt?.accessToken) {
    throw new Error("SwypeSDK auth not ready. Missing access token.");
  }

  const client = axios.create({
    baseURL: `${baseUrl}${apiPath}`,
  });

  /**
   * Request Interceptor
   * -------------------
   * Runs before each request.
   * 1. Checks if token is valid (`ensureValidAuth()`).
   * 2. If not valid, refreshes (`refreshTokenIfNeeded()`).
   * 3. Applies updated headers from store.
   */
  client.interceptors.request.use(
    async (config) => {
      try {
        await ensureValidAuth();
      } catch {
        // Token expired → attempt refresh
        try {
          await refreshTokenIfNeeded();
        } catch (err) {
          clearJWT();
          throw err;
        }
      }
      return applyAuthHeaders(config);
    },
    (error) => Promise.reject(error)
  );

  /**
   * Response Interceptor
   * --------------------
   * Handles API errors globally.
   * 1. If a 401/403 occurs, it attempts one token refresh + retry.
   * 2. If refresh fails, clears auth state to force re-login.
   */
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const { response, config } = error;

      if (
        response &&
        config &&
        (response.status === 401 || response.status === 403)
      ) {
        try {
          await refreshTokenIfNeeded();
          return client(applyAuthHeaders(config));
        } catch (refreshError) {
          clearJWT();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
}
